import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { FileBarChart, Download, Calendar, FileText, Users, ShoppingCart, Factory } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Reports() {
  const [dateRange, setDateRange] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });
  const [production, setProduction] = useState([]);
  const [sales, setSales] = useState([]);
  const [salary, setSalary] = useState([]);
  const [stock, setStock] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [prodRes, salesRes, salaryRes, stockRes, workersRes] = await Promise.all([
        axios.get(`${API}/production?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`, { headers }),
        axios.get(`${API}/sales?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`, { headers }),
        axios.get(`${API}/salary?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`, { headers }),
        axios.get(`${API}/stock`, { headers }),
        axios.get(`${API}/workers`, { headers })
      ]);
      setProduction(prodRes.data);
      setSales(salesRes.data);
      setSalary(salaryRes.data);
      setStock(stockRes.data);
      setWorkers(workersRes.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to fetch report data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // PDF Generation Functions
  const generateProductionPDF = () => {
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(20);
    doc.setTextColor(139, 69, 19);
    doc.text('DHAYA BRICKS', 105, 20, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text('Production Report', 105, 30, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Period: ${formatDate(dateRange.start_date)} to ${formatDate(dateRange.end_date)}`, 105, 38, { align: 'center' });
    doc.text(`Generated: ${new Date().toLocaleString('en-IN')}`, 105, 44, { align: 'center' });

    // Production Summary
    const prodTotals = {
      fly_ash: production.reduce((acc, p) => acc + p.fly_ash, 0),
      interlock_6: production.reduce((acc, p) => acc + p.interlock_6, 0),
      interlock_8: production.reduce((acc, p) => acc + p.interlock_8, 0)
    };

    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text('Summary:', 14, 55);
    doc.setFontSize(10);
    doc.text(`Total Fly Ash: ${prodTotals.fly_ash.toLocaleString()}`, 14, 62);
    doc.text(`Total 6" Interlock: ${prodTotals.interlock_6.toLocaleString()}`, 14, 68);
    doc.text(`Total 8" Interlock: ${prodTotals.interlock_8.toLocaleString()}`, 14, 74);
    doc.text(`Grand Total: ${(prodTotals.fly_ash + prodTotals.interlock_6 + prodTotals.interlock_8).toLocaleString()}`, 14, 80);

    // Production Table
    const tableData = production.map(p => {
      const worker = workers.find(w => w.id === p.worker_id);
      const otherMaterials = (p.other_materials || []).map(om => `${om.name}: ${om.quantity}`).join(', ');
      return [
        formatDate(p.date),
        worker?.name || 'Unknown',
        p.fly_ash,
        p.interlock_6,
        p.interlock_8,
        otherMaterials || '-',
        p.fly_ash + p.interlock_6 + p.interlock_8
      ];
    });

    autoTable(doc, {
      startY: 88,
      head: [['Date', 'Worker', 'Fly Ash', '6" IL', '8" IL', 'Other Materials', 'Total']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [139, 69, 19], textColor: 255 },
      styles: { fontSize: 8, cellPadding: 2 },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 30 },
        5: { cellWidth: 40 }
      }
    });

    doc.save(`Production_Report_${dateRange.start_date}_to_${dateRange.end_date}.pdf`);
    toast.success('Production report downloaded!');
  };

  const generateSalesPDF = () => {
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(20);
    doc.setTextColor(139, 69, 19);
    doc.text('DHAYA BRICKS', 105, 20, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text('Sales Report', 105, 30, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Period: ${formatDate(dateRange.start_date)} to ${formatDate(dateRange.end_date)}`, 105, 38, { align: 'center' });
    doc.text(`Generated: ${new Date().toLocaleString('en-IN')}`, 105, 44, { align: 'center' });

    // Sales Summary
    const salesTotals = {
      fly_ash: sales.reduce((acc, s) => acc + s.fly_ash, 0),
      interlock_6: sales.reduce((acc, s) => acc + s.interlock_6, 0),
      interlock_8: sales.reduce((acc, s) => acc + s.interlock_8, 0),
      total_amount: sales.reduce((acc, s) => acc + (s.total_amount || 0), 0),
      amount_paid: sales.reduce((acc, s) => acc + s.amount_paid, 0),
      amount_due: sales.reduce((acc, s) => acc + (s.amount_due || 0), 0)
    };

    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text('Summary:', 14, 55);
    doc.setFontSize(10);
    doc.text(`Total Fly Ash Sold: ${salesTotals.fly_ash.toLocaleString()}`, 14, 62);
    doc.text(`Total 6" Interlock Sold: ${salesTotals.interlock_6.toLocaleString()}`, 14, 68);
    doc.text(`Total 8" Interlock Sold: ${salesTotals.interlock_8.toLocaleString()}`, 14, 74);
    doc.text(`Total Sales Amount: Rs. ${salesTotals.total_amount.toLocaleString()}`, 14, 80);
    doc.text(`Amount Received: Rs. ${salesTotals.amount_paid.toLocaleString()}`, 14, 86);
    doc.text(`Amount Due: Rs. ${salesTotals.amount_due.toLocaleString()}`, 14, 92);

    // Sales Table
    const tableData = sales.map(s => {
      const otherMaterials = (s.other_materials || []).map(om => `${om.name}`).join(', ');
      return [
        formatDate(s.date),
        s.customer_name,
        s.customer_phone,
        s.fly_ash || 0,
        s.interlock_6 || 0,
        s.interlock_8 || 0,
        otherMaterials || '-',
        `Rs. ${(s.total_amount || 0).toLocaleString()}`,
        `Rs. ${s.amount_paid.toLocaleString()}`,
        s.payment_status
      ];
    });

    autoTable(doc, {
      startY: 100,
      head: [['Date', 'Customer', 'Phone', 'FA', '6"IL', '8"IL', 'Other', 'Total', 'Paid', 'Status']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [139, 69, 19], textColor: 255 },
      styles: { fontSize: 7, cellPadding: 1.5 },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 25 },
        2: { cellWidth: 22 },
        6: { cellWidth: 20 }
      }
    });

    doc.save(`Sales_Report_${dateRange.start_date}_to_${dateRange.end_date}.pdf`);
    toast.success('Sales report downloaded!');
  };

  const generateSalaryPDF = () => {
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(20);
    doc.setTextColor(139, 69, 19);
    doc.text('DHAYA BRICKS', 105, 20, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text('Salary Report', 105, 30, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Period: ${formatDate(dateRange.start_date)} to ${formatDate(dateRange.end_date)}`, 105, 38, { align: 'center' });
    doc.text(`Generated: ${new Date().toLocaleString('en-IN')}`, 105, 44, { align: 'center' });

    // Salary Summary
    const salaryTotals = {
      total_production: salary.reduce((acc, s) => acc + s.total_salary, 0),
      total_loading: salary.reduce((acc, s) => acc + s.loading_earnings, 0),
      total_advances: salary.reduce((acc, s) => acc + s.total_advances, 0),
      total_paid: salary.reduce((acc, s) => acc + s.salary_paid, 0),
      total_balance: salary.reduce((acc, s) => acc + s.final_salary, 0)
    };

    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text('Summary:', 14, 55);
    doc.setFontSize(10);
    doc.text(`Total Production Salary: Rs. ${salaryTotals.total_production.toLocaleString()}`, 14, 62);
    doc.text(`Total Loading Earnings: Rs. ${salaryTotals.total_loading.toLocaleString()}`, 14, 68);
    doc.text(`Total Advances Given: Rs. ${salaryTotals.total_advances.toLocaleString()}`, 14, 74);
    doc.text(`Total Salary Paid: Rs. ${salaryTotals.total_paid.toLocaleString()}`, 14, 80);
    doc.text(`Total Balance Payable: Rs. ${salaryTotals.total_balance.toLocaleString()}`, 14, 86);

    // Salary Table
    const tableData = salary.map(s => [
      s.worker_name,
      s.fly_ash_count,
      s.interlock_6_count,
      s.interlock_8_count,
      `Rs. ${s.total_salary.toLocaleString()}`,
      `Rs. ${s.loading_earnings.toLocaleString()}`,
      `Rs. ${s.total_advances.toLocaleString()}`,
      `Rs. ${s.salary_paid.toLocaleString()}`,
      `Rs. ${s.final_salary.toLocaleString()}`
    ]);

    autoTable(doc, {
      startY: 94,
      head: [['Worker', 'Fly Ash', '6" IL', '8" IL', 'Production', 'Loading', 'Advances', 'Paid', 'Balance']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [139, 69, 19], textColor: 255 },
      styles: { fontSize: 8, cellPadding: 2 },
      foot: [[
        'TOTAL',
        salary.reduce((acc, s) => acc + s.fly_ash_count, 0),
        salary.reduce((acc, s) => acc + s.interlock_6_count, 0),
        salary.reduce((acc, s) => acc + s.interlock_8_count, 0),
        `Rs. ${salaryTotals.total_production.toLocaleString()}`,
        `Rs. ${salaryTotals.total_loading.toLocaleString()}`,
        `Rs. ${salaryTotals.total_advances.toLocaleString()}`,
        `Rs. ${salaryTotals.total_paid.toLocaleString()}`,
        `Rs. ${salaryTotals.total_balance.toLocaleString()}`
      ]],
      footStyles: { fillColor: [240, 240, 240], textColor: 0, fontStyle: 'bold' }
    });

    doc.save(`Salary_Report_${dateRange.start_date}_to_${dateRange.end_date}.pdf`);
    toast.success('Salary report downloaded!');
  };

  const generateAllReportsPDF = () => {
    const doc = new jsPDF();
    let yPos = 20;

    // ========== PAGE 1: SUMMARY ==========
    doc.setFontSize(22);
    doc.setTextColor(139, 69, 19);
    doc.text('DHAYA BRICKS', 105, yPos, { align: 'center' });
    
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text('Complete Business Report', 105, yPos + 12, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Period: ${formatDate(dateRange.start_date)} to ${formatDate(dateRange.end_date)}`, 105, yPos + 22, { align: 'center' });
    doc.text(`Generated: ${new Date().toLocaleString('en-IN')}`, 105, yPos + 28, { align: 'center' });

    // Stock Summary
    yPos = 55;
    doc.setFontSize(14);
    doc.setTextColor(139, 69, 19);
    doc.text('Current Stock Status', 14, yPos);
    
    autoTable(doc, {
      startY: yPos + 5,
      head: [['Item', 'Quantity']],
      body: [
        ['Fly Ash Bricks', stock?.fly_ash?.toLocaleString() || 0],
        ['6" Interlock Bricks', stock?.interlock_6?.toLocaleString() || 0],
        ['8" Interlock Bricks', stock?.interlock_8?.toLocaleString() || 0],
        ['Cement (kg)', stock?.cement?.toLocaleString() || 0],
        ...Object.entries(stock?.custom_materials || {}).map(([name, qty]) => [name, qty.toLocaleString()])
      ],
      theme: 'grid',
      headStyles: { fillColor: [139, 69, 19] },
      styles: { fontSize: 9 },
      tableWidth: 80
    });

    // ========== PAGE 2: PRODUCTION ==========
    doc.addPage();
    doc.setFontSize(16);
    doc.setTextColor(139, 69, 19);
    doc.text('Production Report', 105, 20, { align: 'center' });

    const prodTotals = {
      fly_ash: production.reduce((acc, p) => acc + p.fly_ash, 0),
      interlock_6: production.reduce((acc, p) => acc + p.interlock_6, 0),
      interlock_8: production.reduce((acc, p) => acc + p.interlock_8, 0)
    };

    const prodTableData = production.map(p => {
      const worker = workers.find(w => w.id === p.worker_id);
      return [
        formatDate(p.date),
        worker?.name || 'Unknown',
        p.fly_ash,
        p.interlock_6,
        p.interlock_8,
        p.fly_ash + p.interlock_6 + p.interlock_8
      ];
    });

    autoTable(doc, {
      startY: 30,
      head: [['Date', 'Worker', 'Fly Ash', '6" IL', '8" IL', 'Total']],
      body: prodTableData,
      foot: [['TOTAL', '', prodTotals.fly_ash, prodTotals.interlock_6, prodTotals.interlock_8, 
              prodTotals.fly_ash + prodTotals.interlock_6 + prodTotals.interlock_8]],
      theme: 'grid',
      headStyles: { fillColor: [139, 69, 19] },
      footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold' },
      styles: { fontSize: 8 }
    });

    // ========== PAGE 3: SALES ==========
    doc.addPage();
    doc.setFontSize(16);
    doc.setTextColor(139, 69, 19);
    doc.text('Sales Report', 105, 20, { align: 'center' });

    const salesTableData = sales.map(s => [
      formatDate(s.date),
      s.customer_name,
      s.fly_ash || 0,
      s.interlock_6 || 0,
      s.interlock_8 || 0,
      `Rs. ${(s.total_amount || 0).toLocaleString()}`,
      `Rs. ${s.amount_paid.toLocaleString()}`,
      s.payment_status
    ]);

    const salesTotals = {
      total: sales.reduce((acc, s) => acc + (s.total_amount || 0), 0),
      paid: sales.reduce((acc, s) => acc + s.amount_paid, 0)
    };

    autoTable(doc, {
      startY: 30,
      head: [['Date', 'Customer', 'FA', '6"IL', '8"IL', 'Total', 'Paid', 'Status']],
      body: salesTableData,
      foot: [['TOTAL', '', '', '', '', `Rs. ${salesTotals.total.toLocaleString()}`, 
              `Rs. ${salesTotals.paid.toLocaleString()}`, '']],
      theme: 'grid',
      headStyles: { fillColor: [139, 69, 19] },
      footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold' },
      styles: { fontSize: 8 }
    });

    // ========== PAGE 4: SALARY ==========
    doc.addPage();
    doc.setFontSize(16);
    doc.setTextColor(139, 69, 19);
    doc.text('Salary Report', 105, 20, { align: 'center' });

    const salaryTableData = salary.map(s => [
      s.worker_name,
      s.fly_ash_count,
      s.interlock_6_count,
      s.interlock_8_count,
      `Rs. ${s.total_salary.toLocaleString()}`,
      `Rs. ${s.loading_earnings.toLocaleString()}`,
      `Rs. ${s.total_advances.toLocaleString()}`,
      `Rs. ${s.final_salary.toLocaleString()}`
    ]);

    const salaryTotals = {
      production: salary.reduce((acc, s) => acc + s.total_salary, 0),
      loading: salary.reduce((acc, s) => acc + s.loading_earnings, 0),
      advances: salary.reduce((acc, s) => acc + s.total_advances, 0),
      balance: salary.reduce((acc, s) => acc + s.final_salary, 0)
    };

    autoTable(doc, {
      startY: 30,
      head: [['Worker', 'FA', '6"IL', '8"IL', 'Production', 'Loading', 'Advances', 'Balance']],
      body: salaryTableData,
      foot: [['TOTAL', '', '', '', `Rs. ${salaryTotals.production.toLocaleString()}`, 
              `Rs. ${salaryTotals.loading.toLocaleString()}`, `Rs. ${salaryTotals.advances.toLocaleString()}`,
              `Rs. ${salaryTotals.balance.toLocaleString()}`]],
      theme: 'grid',
      headStyles: { fillColor: [139, 69, 19] },
      footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold' },
      styles: { fontSize: 8 }
    });

    doc.save(`Complete_Report_${dateRange.start_date}_to_${dateRange.end_date}.pdf`);
    toast.success('Complete report downloaded!');
  };

  // Calculate totals
  const prodTotals = {
    fly_ash: production.reduce((acc, p) => acc + p.fly_ash, 0),
    interlock_6: production.reduce((acc, p) => acc + p.interlock_6, 0),
    interlock_8: production.reduce((acc, p) => acc + p.interlock_8, 0)
  };

  const salesTotals = {
    fly_ash: sales.reduce((acc, s) => acc + s.fly_ash, 0),
    interlock_6: sales.reduce((acc, s) => acc + s.interlock_6, 0),
    interlock_8: sales.reduce((acc, s) => acc + s.interlock_8, 0),
    revenue: sales.reduce((acc, s) => acc + s.amount_paid, 0)
  };

  return (
    <div data-testid="reports-page" className="space-y-8">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="font-manrope font-bold text-4xl text-concrete-900 tracking-tight" data-testid="reports-title">
            <FileBarChart className="inline-block mr-3 h-10 w-10 text-brick-600" />
            Reports
          </h1>
          <p className="font-inter text-concrete-600 mt-2">Generate and download PDF reports</p>
        </div>
      </div>

      {/* Date Range Selection */}
      <div className="bg-white border border-concrete-200 shadow-sm rounded-md p-6">
        <h3 className="font-manrope font-bold text-lg mb-4">Select Date Range</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <Label className="font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Start Date</Label>
            <Input
              type="date"
              value={dateRange.start_date}
              onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
              className="mt-2 border-concrete-300 focus:border-brick-500"
            />
          </div>
          <div>
            <Label className="font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">End Date</Label>
            <Input
              type="date"
              value={dateRange.end_date}
              onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
              className="mt-2 border-concrete-300 focus:border-brick-500"
            />
          </div>
          <Button onClick={fetchReport} disabled={loading} className="bg-brick-600 hover:bg-brick-500">
            {loading ? 'Loading...' : 'Generate Report'}
          </Button>
        </div>
      </div>

      {/* PDF Download Buttons */}
      <div className="bg-white border border-concrete-200 shadow-sm rounded-md p-6">
        <h3 className="font-manrope font-bold text-lg mb-4">Download PDF Reports</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Button
            onClick={generateProductionPDF}
            disabled={loading || production.length === 0}
            variant="outline"
            className="flex items-center justify-center gap-2 h-20 border-2 hover:border-brick-500 hover:bg-brick-50"
          >
            <Factory className="h-6 w-6 text-brick-600" />
            <div className="text-left">
              <p className="font-semibold">Production Report</p>
              <p className="text-xs text-concrete-500">{production.length} entries</p>
            </div>
          </Button>

          <Button
            onClick={generateSalesPDF}
            disabled={loading || sales.length === 0}
            variant="outline"
            className="flex items-center justify-center gap-2 h-20 border-2 hover:border-green-500 hover:bg-green-50"
          >
            <ShoppingCart className="h-6 w-6 text-green-600" />
            <div className="text-left">
              <p className="font-semibold">Sales Report</p>
              <p className="text-xs text-concrete-500">{sales.length} entries</p>
            </div>
          </Button>

          <Button
            onClick={generateSalaryPDF}
            disabled={loading || salary.length === 0}
            variant="outline"
            className="flex items-center justify-center gap-2 h-20 border-2 hover:border-blue-500 hover:bg-blue-50"
          >
            <Users className="h-6 w-6 text-blue-600" />
            <div className="text-left">
              <p className="font-semibold">Salary Report</p>
              <p className="text-xs text-concrete-500">{salary.length} workers</p>
            </div>
          </Button>

          <Button
            onClick={generateAllReportsPDF}
            disabled={loading}
            className="flex items-center justify-center gap-2 h-20 bg-brick-600 hover:bg-brick-500"
          >
            <FileText className="h-6 w-6" />
            <div className="text-left">
              <p className="font-semibold">Complete Report</p>
              <p className="text-xs text-brick-200">All in one PDF</p>
            </div>
          </Button>
        </div>
      </div>

      {/* Report Preview */}
      {!loading && (
        <Tabs defaultValue="summary" className="w-full">
          <TabsList className="bg-white border border-concrete-200 mb-4">
            <TabsTrigger value="summary" className="font-manrope font-semibold">Summary</TabsTrigger>
            <TabsTrigger value="production" className="font-manrope font-semibold">Production ({production.length})</TabsTrigger>
            <TabsTrigger value="sales" className="font-manrope font-semibold">Sales ({sales.length})</TabsTrigger>
            <TabsTrigger value="salary" className="font-manrope font-semibold">Salary ({salary.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="summary">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white border border-concrete-200 shadow-sm rounded-md p-6">
                <h3 className="font-manrope font-bold text-lg text-concrete-900 mb-4 flex items-center gap-2">
                  <Factory className="h-5 w-5 text-brick-600" />
                  Production Total
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-concrete-600">Fly Ash</span>
                    <span className="font-semibold">{prodTotals.fly_ash.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-concrete-600">6" Interlock</span>
                    <span className="font-semibold">{prodTotals.interlock_6.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-concrete-600">8" Interlock</span>
                    <span className="font-semibold">{prodTotals.interlock_8.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="font-bold">Total</span>
                    <span className="font-bold text-brick-600">
                      {(prodTotals.fly_ash + prodTotals.interlock_6 + prodTotals.interlock_8).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-concrete-200 shadow-sm rounded-md p-6">
                <h3 className="font-manrope font-bold text-lg text-concrete-900 mb-4 flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5 text-green-600" />
                  Sales Total
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-concrete-600">Fly Ash</span>
                    <span className="font-semibold">{salesTotals.fly_ash.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-concrete-600">6" Interlock</span>
                    <span className="font-semibold">{salesTotals.interlock_6.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-concrete-600">8" Interlock</span>
                    <span className="font-semibold">{salesTotals.interlock_8.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="font-bold">Revenue</span>
                    <span className="font-bold text-green-600">₹{salesTotals.revenue.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-concrete-200 shadow-sm rounded-md p-6">
                <h3 className="font-manrope font-bold text-lg text-concrete-900 mb-4 flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-600" />
                  Salary Total
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-concrete-600">Production</span>
                    <span className="font-semibold">₹{salary.reduce((acc, s) => acc + s.total_salary, 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-concrete-600">Loading</span>
                    <span className="font-semibold">₹{salary.reduce((acc, s) => acc + s.loading_earnings, 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-concrete-600">Advances</span>
                    <span className="font-semibold text-red-600">-₹{salary.reduce((acc, s) => acc + s.total_advances, 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="font-bold">Balance</span>
                    <span className="font-bold text-blue-600">₹{salary.reduce((acc, s) => acc + s.final_salary, 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="production">
            <div className="bg-white border border-concrete-200 shadow-sm rounded-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-concrete-50 border-b border-concrete-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-concrete-700 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-concrete-700 uppercase">Worker</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-concrete-700 uppercase">Fly Ash</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-concrete-700 uppercase">6" IL</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-concrete-700 uppercase">8" IL</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-concrete-700 uppercase">Other</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-concrete-700 uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-concrete-100">
                    {production.length === 0 ? (
                      <tr><td colSpan="7" className="px-4 py-8 text-center text-concrete-500">No production data</td></tr>
                    ) : (
                      production.map(p => {
                        const worker = workers.find(w => w.id === p.worker_id);
                        return (
                          <tr key={p.id} className="hover:bg-concrete-50">
                            <td className="px-4 py-3 text-sm">{formatDate(p.date)}</td>
                            <td className="px-4 py-3 text-sm font-medium">{worker?.name || 'Unknown'}</td>
                            <td className="px-4 py-3 text-sm text-right tabular-nums">{p.fly_ash}</td>
                            <td className="px-4 py-3 text-sm text-right tabular-nums">{p.interlock_6}</td>
                            <td className="px-4 py-3 text-sm text-right tabular-nums">{p.interlock_8}</td>
                            <td className="px-4 py-3 text-sm text-purple-600">
                              {(p.other_materials || []).map(om => `${om.name}: ${om.quantity}`).join(', ') || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-right tabular-nums font-semibold text-brick-600">
                              {p.fly_ash + p.interlock_6 + p.interlock_8}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="sales">
            <div className="bg-white border border-concrete-200 shadow-sm rounded-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-concrete-50 border-b border-concrete-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-concrete-700 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-concrete-700 uppercase">Customer</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-concrete-700 uppercase">Bricks</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-concrete-700 uppercase">Total</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-concrete-700 uppercase">Paid</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-concrete-700 uppercase">Due</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-concrete-700 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-concrete-100">
                    {sales.length === 0 ? (
                      <tr><td colSpan="7" className="px-4 py-8 text-center text-concrete-500">No sales data</td></tr>
                    ) : (
                      sales.map(s => (
                        <tr key={s.id} className="hover:bg-concrete-50">
                          <td className="px-4 py-3 text-sm">{formatDate(s.date)}</td>
                          <td className="px-4 py-3 text-sm font-medium">{s.customer_name}</td>
                          <td className="px-4 py-3 text-sm text-right tabular-nums">{s.fly_ash + s.interlock_6 + s.interlock_8}</td>
                          <td className="px-4 py-3 text-sm text-right tabular-nums">₹{(s.total_amount || 0).toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-right tabular-nums text-green-600">₹{s.amount_paid.toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-right tabular-nums text-red-600">₹{(s.amount_due || 0).toLocaleString()}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              s.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                              s.payment_status === 'partial' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                            }`}>{s.payment_status}</span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="salary">
            <div className="bg-white border border-concrete-200 shadow-sm rounded-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-concrete-50 border-b border-concrete-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-concrete-700 uppercase">Worker</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-concrete-700 uppercase">Fly Ash</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-concrete-700 uppercase">6" IL</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-concrete-700 uppercase">8" IL</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-concrete-700 uppercase">Production</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-concrete-700 uppercase">Loading</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-concrete-700 uppercase">Advances</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-concrete-700 uppercase">Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-concrete-100">
                    {salary.length === 0 ? (
                      <tr><td colSpan="8" className="px-4 py-8 text-center text-concrete-500">No salary data for selected period</td></tr>
                    ) : (
                      salary.map(s => (
                        <tr key={s.worker_id} className="hover:bg-concrete-50">
                          <td className="px-4 py-3 text-sm font-medium">{s.worker_name}</td>
                          <td className="px-4 py-3 text-sm text-right tabular-nums">{s.fly_ash_count}</td>
                          <td className="px-4 py-3 text-sm text-right tabular-nums">{s.interlock_6_count}</td>
                          <td className="px-4 py-3 text-sm text-right tabular-nums">{s.interlock_8_count}</td>
                          <td className="px-4 py-3 text-sm text-right tabular-nums">₹{s.total_salary.toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-right tabular-nums text-green-600">₹{s.loading_earnings.toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-right tabular-nums text-red-600">₹{s.total_advances.toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-right tabular-nums font-semibold text-blue-600">₹{s.final_salary.toLocaleString()}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
